/**
 * The daily practice reminder the dashboard and Practice page offer: an email,
 * or in the iOS app a notification (pushNotifications.ts)
 */
import * as preact from "preact";

import * as deviceDetect from "deviceDetect";
import * as notificationUtils from "notificationUtils";
import * as pushNotifications from "pushNotifications";
import stores from "stores/stores";

export const byNotification = () => pushNotifications.isApp();

const type = () => (byNotification() ? "dailyPushReminder" : "dailyReminder");

export const isEnabled = (language: string) =>
  notificationUtils.has(type(), language);

/** Turns the reminder on, in the app once notifications are allowed: whether it's on */
export const enable = async () => {
  if (
    byNotification() &&
    !(await pushNotifications.subscribe(deviceDetect.deviceString()))
  ) {
    alert(pushNotifications.deniedMessage());
    return false;
  }
  notificationUtils.addType(type());
  return true;
};

export const disable = () => notificationUtils.removeType(type());

/**
 * Whether this device gets the reminders: notifications allowed, and the
 * server sending them here (it may not be, e.g. reminders turned on from
 * another device, or this one signed in to another server)
 */
const thisDeviceNotified = async () => {
  const status = await pushNotifications.getSubscriptionStatus();
  return (
    status.type === "haveSubscription" &&
    (await pushNotifications.getServerSubscriptions(status)).some(
      (subscription) => subscription.thisDevice
    )
  );
};

/**
 * The reminder's switch, staying as it's turned on or off (Home, Practice).
 * In the app it's on only while this device gets them (thisDeviceNotified).
 */
export class DailyReminderSwitch extends preact.Component<
  { label: string },
  { notificationsOff: boolean }
> {
  state = { notificationsOff: false };

  async componentDidMount() {
    if (this.isOn() && byNotification()) {
      this.setState({ notificationsOff: !(await thisDeviceNotified()) });
    }
  }

  private isOn() {
    return (
      isEnabled(stores.user.currentLearningLanguage.value) &&
      !this.state.notificationsOff
    );
  }

  private toggle = async (event: MouseEvent) => {
    event.stopPropagation();
    if (this.isOn()) {
      disable();
    } else if (await enable()) {
      this.setState({ notificationsOff: false });
    }
    this.forceUpdate();
  };

  render() {
    return (
      <label class="switchRow">
        <span>{this.props.label}</span>
        <input
          class="switch"
          type="checkbox"
          name="dailyReminder"
          checked={this.isOn()}
          onClick={this.toggle}
        />
      </label>
    );
  }
}
